const DEFAULT_DEDUPE_WINDOW_MS = 5_000;

const errorEventIds = new WeakMap<Error, string>();
const recentFingerprints = new Map<string, number>();

let eventSequence = 0;

function randomFragment(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID().replace(/-/g, '').slice(0, 12);
  }

  return Math.random().toString(36).slice(2, 14);
}

function normalizePart(part: unknown): string {
  if (part == null) return '';
  if (typeof part === 'string') return part.trim();
  if (typeof part === 'number' || typeof part === 'boolean') return String(part);

  try {
    return JSON.stringify(part);
  } catch {
    return String(part);
  }
}

function hashString(input: string): string {
  let hash = 5381;

  for (let i = 0; i < input.length; i += 1) {
    hash = ((hash << 5) + hash) ^ input.charCodeAt(i);
  }

  return (hash >>> 0).toString(36);
}

function cleanupRecentFingerprints(now: number, dedupeWindowMs: number): void {
  for (const [fingerprint, timestamp] of recentFingerprints.entries()) {
    if (now - timestamp > dedupeWindowMs) {
      recentFingerprints.delete(fingerprint);
    }
  }
}

export function newEventId(prefix = 'evt'): string {
  eventSequence = (eventSequence + 1) % 1_000_000;
  return `${prefix}_${Date.now().toString(36)}_${eventSequence.toString(36)}_${randomFragment()}`;
}

export function getErrorEventId(error: Error): string {
  const existing = errorEventIds.get(error);
  if (existing) return existing;

  const next = newEventId('err');
  errorEventIds.set(error, next);
  return next;
}

export function buildFingerprint(parts: unknown[]): string {
  const normalized = parts
    .map(normalizePart)
    .filter(Boolean)
    .join('|')
    .replace(/\s+/g, ' ')
    .slice(0, 2_000);

  if (!normalized) {
    return `fp_${hashString('empty')}`;
  }

  return `fp_${hashString(normalized)}`;
}

export function shouldReportFingerprint(
  fingerprint: string,
  dedupeWindowMs = DEFAULT_DEDUPE_WINDOW_MS
): boolean {
  const now = Date.now();
  cleanupRecentFingerprints(now, dedupeWindowMs);

  const lastSeen = recentFingerprints.get(fingerprint);
  if (lastSeen && now - lastSeen < dedupeWindowMs) {
    return false;
  }

  recentFingerprints.set(fingerprint, now);
  return true;
}
